import { motion } from 'framer-motion';
import Section from '../components/Section';
import PageHero from '../components/PageHero';
import SurfaceCard from '../components/SurfaceCard';
import { SF_UI } from '../components/ui/tokens';

const LegalPage = () => {
  return (
    <>
      <PageHero
        eyebrow="Support"
        title="Legal Information"
        subtitle="Important legal information about SportsFuture Movement"
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <div className="prose prose-lg max-w-none">
          <SurfaceCard className="mb-8" animate={false}>
            <h2 className="text-2xl font-bold mb-4">Legal Disclaimer for SportsFuture Movement</h2>
            <p>
              The SportsFuture Movement ("SportsFuture") operates as a decentralized platform dedicated to supporting sports athletes through memberships, fundraising, and related activities. By accessing or using the SportsFuture website, participating in memberships, or contributing to fundraising efforts, you agree to be bound by this Legal Disclaimer and all terms outlined in the DAO1 legal documents, which apply fully and without exception to all SportsFuture offerings.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">Compliance with DAO1 Legal Documents</h3>
            <p>
              All activities, memberships, and fundraising conducted through SportsFuture are governed by the legal framework and rules established in the DAO1 legal documents. These documents take precedence and are binding in their entirety. Users are responsible for reviewing and understanding these documents before engaging with SportsFuture.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">Membership and Fundraising</h3>
            <p>
              Participation in SportsFuture memberships is entirely voluntary. Funds collected through memberships are allocated as follows:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>90% directly supports sports athletes through funding initiatives.</li>
              <li>10% covers operational and platform-related costs.</li>
            </ul>
            <p>
              SportsFuture makes no guarantees regarding the outcomes of funded initiatives or the performance of supported athletes. Contributions are made at the participant's own risk.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">Geographic Restrictions</h3>
            <p>
              SportsFuture memberships, fundraising, and services are not offered in the United States or Canada until further notice. Any attempt to access, participate in, or use SportsFuture offerings from these jurisdictions is strictly prohibited and will not be accepted under any circumstances. SportsFuture reserves the right to restrict access and take appropriate action to enforce this restriction.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">No Liability</h3>
            <p>
              SportsFuture, its affiliates, and its operators shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of the website, memberships, or fundraising activities. Participants engage with SportsFuture at their own risk and discretion.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">Changes to Disclaimer</h3>
            <p>
              SportsFuture reserves the right to modify this Legal Disclaimer at any time without prior notice. Continued use of the platform constitutes acceptance of any updated terms.
            </p>

            <p className="mt-6">
              For further details on the DAO1 legal documents or SportsFuture's operations, please contact our Sports Office Officer through the official website contactlist.
            </p>

            <p className="text-sm text-gray-600 mt-6">Last Updated: April 28, 2025</p>
          </SurfaceCard>

          <SurfaceCard className="mb-8" animate={false}>
            <h2 className="text-2xl font-bold mb-6">NFT Sales – Sports Offices</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold mb-3">1. Non-Investment Disclaimer</h3>
                <p className="text-gray-700">
                  The NFTs sold through the SportsFuture platform are digital collectibles intended for personal enjoyment, virtual access, community representation, and interactive experiences only. They do not represent equity, shares, securities, or financial instruments of any kind.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-3">2. No Expectation of Profit</h3>
                <p className="text-gray-700">
                  Purchasers should not expect any profits, returns, or financial rewards from holding or reselling these NFTs. The value of each NFT is subjective and determined by community interest, use cases, and artistic value.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-3">3. Utility-Only Airdrops</h3>
                <p className="text-gray-700">
                  From time to time, SportsFuture may distribute utility tokens (SFUT) or rewards for platform engagement, holding NFTs, or participation in campaigns. These distributions are not guaranteed, not performance-based, and serve only as loyalty or gamification incentives.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-3">4. Jurisdictional Restrictions</h3>
                <p className="text-gray-700">
                  This platform does not offer services to users in jurisdictions where such digital assets are classified as securities, investment products, or are otherwise regulated in ways that conflict with this platform's purpose. Please consult with your local legal advisor before purchasing.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-3">5. NFT Purpose</h3>
                <p className="text-gray-700">
                  Each NFT represents a visual and symbolic representation of a Sports Office location and is designed to promote sport, inclusivity, and participation in the SportsFuture ecosystem. Ownership of an NFT grants access to virtual experiences, but does not confer governance rights, revenue shares, or investment interests unless explicitly stated.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-3">6. Responsibility</h3>
                <p className="text-gray-700">
                  Buyers acknowledge that all transactions are performed on a decentralized platform and are non-refundable, irreversible, and conducted at their own discretion and risk.
                </p>
              </div>
            </div>
          </SurfaceCard>

          <SurfaceCard animate={false}>
            <h2 className="text-2xl font-bold mb-6">Legal Documents</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold mb-2">DAO1 Legal Framework</h3>
                <div className="space-y-2">
                  <a href="https://app.dao1.ai/web-conditions" target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline">
                    Web Conditions
                  </a>
                  <a href="https://app.dao1.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline">
                    Terms of Service
                  </a>
                  <a href="https://app.dao1.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline">
                    Privacy Policy
                  </a>
                  <a href="https://app.dao1.ai/affiliate-agreement" target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline">
                    Affiliate Agreement
                  </a>
                  <a href="https://app.dao1.ai/principal-guidance" target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline">
                    Principal Guidance
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-2">Blockchain Information</h3>
                <a href="https://avascan.info/blockchain/apertum/info" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  View Apertum Subnet on Avascan
                </a>
              </div>
            </div>
          </SurfaceCard>
        </div>
      </motion.div>
      </Section>
    </>
  );
};

export default LegalPage;